import { useCallback, useRef, useState } from 'react';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
  CreatedCashierBoxMovementContractError,
  useAddCashierBoxMovementV2,
} from '../../hooks/cashierboxes/mutations';
import { showNotification } from '../../utils/Notification/Notification';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import PaymentConfigEnum from '../../enum/PaymentConfig';
import type { IInOutMovement } from '../../interfaces/Store';
import type { AddCashierBoxMovementV2Payload } from '../../services/store.service';

export const getCashierBoxMovementInitialValues = () => ({
  type: '',
  date: new Date(),
  amount: '' as number | '',
  paymentMethod: PaymentConfigEnum.CASH,
  notes: ''
});

type UseCashierBoxMovementFormParams = {
  storeId: string;
  cashierBoxId: string;
  onSuccess?: () => void;
};

export type CashierBoxMovementFormValues = ReturnType<typeof getCashierBoxMovementInitialValues>;

type SubmitOptions = {
  afterSave?: (movement: IInOutMovement) => Promise<void> | void;
};

export const useCashierBoxMovementForm = ({
  storeId,
  cashierBoxId,
  onSuccess
}: UseCashierBoxMovementFormParams) => {
  const { t } = useTranslation(['Notifications', 'CashierboxesPage']);
  const addMovement = useAddCashierBoxMovementV2(storeId, cashierBoxId);
  const processingLockRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const form = useForm({
    initialValues: getCashierBoxMovementInitialValues(),
    validate: {
      type: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      date: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      amount: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      paymentMethod: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      }
    }
  });

  const resetForm = useCallback(() => {
    const initialValues = getCashierBoxMovementInitialValues();
    form.setValues(initialValues);
    form.resetDirty(initialValues);
    form.clearErrors();
  }, [form]);

  const submit = async (
    values: CashierBoxMovementFormValues,
    options?: SubmitOptions,
  ): Promise<IInOutMovement | null> => {
    if (processingLockRef.current) return null;

    processingLockRef.current = true;
    setIsProcessing(true);

    try {
      const payload: AddCashierBoxMovementV2Payload = {
        type: values.type,
        date: values.date,
        amount: Number(values.amount),
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      };
      const movement = await addMovement.mutateAsync(payload);
      showNotification({ type: 'Default', title: t('Notifications:success') });
      await options?.afterSave?.(movement);
      resetForm();
      if (onSuccess) onSuccess();
      return movement;
    } catch (error) {
      showNotification({
        type: 'Error',
        title:
          error instanceof CreatedCashierBoxMovementContractError
            ? t('CashierboxesPage:movementReceipt.createdMovementContractError')
            : t('Notifications:somethingWrong'),
      });
      return null;
    } finally {
      processingLockRef.current = false;
      setIsProcessing(false);
    }
  };

  return {
    form,
    submit,
    resetForm,
    isSubmitting: addMovement.isPending || isProcessing
  };
};
